import React, { useState, useMemo } from 'react';
import styles from './signature-standalone-field.module.css';
import { FormattedMessage } from 'react-intl';
import { Box, Cluster } from '@dropbox/dig-foundations';
import { Button } from '@dropbox/dig-components/buttons';

import { DisplayField } from 'signer-app/signature-request';
import CONSTANTS from 'signer-app/signature-modal/constants';
import {
  SignatureModalContext,
  useSignatureModalContext,
} from 'signer-app/signature-modal/signature-modal-context/context';
import { signerContext } from 'signer-app/signer-signature-document/context';
import { SignatureField, InitialsField } from 'signer-app/types/editor-types';

import useSignatureData from '../signature-modal/use-signature-data';
import { Tab } from '../signature-modal/modal';
import useInsertSignature from '../signature-modal/use-insert-signature';
import StandaloneField from '../signature-modal/standalone-signature-field';
import { getCommonProps } from './signature-placeholder';
import { SignerContextShape } from './types';

type SignatureStandaloneFieldWrapperProps = {
  fieldData: SignatureField | InitialsField;
};

type SignatureStandaloneFieldProps = {
  fieldData: SignatureField | InitialsField;
  insertSignature: SignerContextShape['insertSignature'];
  signatureCount: number;
  initialsCount: number;
};

function SignatureStandaloneFieldWrapper(
  { fieldData, ...props }: SignatureStandaloneFieldWrapperProps,
  ref: React.ForwardedRef<any>,
) {
  const { insertSignature, signatureCount, initialsCount } =
    React.useContext(signerContext);

  return (
    <SignatureStandaloneField
      ref={ref}
      fieldData={fieldData}
      insertSignature={insertSignature}
      signatureCount={signatureCount}
      initialsCount={initialsCount}
      {...props}
    />
  );
}

export const SignatureStandaloneField = React.forwardRef(
  function SignatureStandaloneField(
    {
      fieldData,
      insertSignature,
      signatureCount,
      initialsCount,
      ...props
    }: SignatureStandaloneFieldProps,
    ref: React.ForwardedRef<any>,
  ) {
    const signatureModalProps = useSignatureModalContext();
    const { startEnabled } = React.useContext(signerContext);

    const [open, setOpen] = useState(true);
    const [isEdit, setIsEdit] = useState(false);
    const [isCreatingSignatureOrInitials, setIsCreatingSignatureOrInitials] =
      useState(false);

    const [selectedTab, setSelectedTab] = useState<Tab>(CONSTANTS.TAB_DRAW);
    const [enableInsert, setEnableInsert] = useState(false);

    const handleOpen = React.useCallback(() => {
      setOpen(startEnabled);
    }, [startEnabled]);

    const handleClose = React.useCallback(() => {
      setOpen(false);
      setIsEdit(false);
      setIsCreatingSignatureOrInitials(false);

      if (
        signatureModalProps.error &&
        signatureModalProps?.clearSignatureError
      ) {
        signatureModalProps.clearSignatureError();
      }
    }, [signatureModalProps]);

    const handleClear = React.useCallback(() => {
      insertSignature(null, fieldData, false);
    }, [fieldData, insertSignature]);

    React.useEffect(() => {
      if (isEdit || (fieldData.signature == null && !fieldData.readOnly)) {
        if (!open) {
          handleOpen();
        }
      } else if (open && !isEdit) {
        handleClose();
      }
    }, [open, isEdit, fieldData, handleOpen, handleClose]);

    const handleInsert = React.useCallback(
      (signature, insertEverywhere) => {
        insertSignature(signature, fieldData, insertEverywhere, true);
        handleClose();
      },
      [fieldData, handleClose, insertSignature],
    );

    const handleSignatureData: SignatureModalContext['createNewSignature'] =
      async (sigData) => {
        // Setting isCreatingSignatureOrInitials to trigger message
        setIsCreatingSignatureOrInitials(true);

        try {
          return await signatureModalProps.createNewSignature(sigData);
        } catch (error) {
          setIsCreatingSignatureOrInitials(false);
          throw error;
        }
      };

    const { selectedSavedSignature, splitProps } = getCommonProps(
      fieldData.type,
      signatureModalProps,
    );

    const canInsertEverywhere =
      signatureModalProps.canInsertEverywhere &&
      (fieldData.type === 'signature' ? signatureCount : initialsCount) > 1;

    const loading = isCreatingSignatureOrInitials;

    const commonSigModalProps = {
      ...signatureModalProps,
      ...splitProps,
      loading,
      canInsertEverywhere,
      selectedSavedSignature,
      onClose: handleClose,
      onInsert: handleInsert,
      createNewSignature: handleSignatureData,
    };

    const onTabSelect = (tab: Tab) => setSelectedTab(tab);

    const { signatureModalBodyRef, getSignatureData } = useSignatureData(
      commonSigModalProps.type,
      selectedTab,
    );

    const {
      insertSignature: insertSignatureCallback,
      insertSignatureEverywhere,
    } = useInsertSignature(
      selectedTab,
      commonSigModalProps.onInsert,
      getSignatureData,
      commonSigModalProps.createNewSignature,
    );

    const isDisabled = useMemo(() => {
      const signatureData = getSignatureData();

      return !enableInsert || signatureData === null || loading;
    }, [enableInsert, loading, getSignatureData]);

    return (
      <>
        {open ? (
          <StandaloneField
            {...commonSigModalProps}
            enableInsertButtonCallback={setEnableInsert}
            selectedTab={selectedTab}
            onTabSelect={onTabSelect}
            signatureModalBodyRef={signatureModalBodyRef}
          />
        ) : (
          <Box
            className={styles.signaturePad}
            width="100%"
            borderStyle="Solid"
            borderColor="Border Base"
            position="relative"
            fontWeight="Strong"
          >
            <Box display="flex" height="100%" width="100%">
              <DisplayField
                {...props}
                ref={ref}
                data-testid={`${fieldData.type}-input`}
              />
            </Box>
          </Box>
        )}

        <Box marginTop="Micro Small" position="relative">
          <Box position="absolute" right="0">
            {open ? (
              <Cluster gap="Micro Small">
                {isEdit && (
                  <Cluster.Item>
                    <Button
                      variant="transparent"
                      onClick={() => setIsEdit(false)}
                    >
                      <FormattedMessage
                        id="51706184e5a39caf83d2439e045dcbc7484f8fb851e0333e676c717a293bcaeb"
                        description="Text for a button that cancels editing a signature."
                        defaultMessage="Cancel"
                      />
                    </Button>
                  </Cluster.Item>
                )}
                {canInsertEverywhere && (
                  <Cluster.Item>
                    <Button
                      variant="opacity"
                      data-testid="insert-everywhere-btn"
                      onClick={() => insertSignatureEverywhere()}
                      disabled={isDisabled}
                    >
                      <Box display={{ small: 'block', xsmall: 'none' }}>
                        <FormattedMessage
                          id="5a3f40ccd6492d50be3d0e4d7550ecce763f9f36a5cc5fa8c8c47c89627085ad"
                          description="Text for a button that inserts the users signature into all signature fields."
                          defaultMessage="Fill in all fields"
                        />
                      </Box>

                      <Box display={{ xsmall: 'block', small: 'none' }}>
                        <FormattedMessage
                          id="7c21a9d3a616cebd3b1f195d216d4c20681cc249a79763004182c1701b8dd778"
                          description="Text for a button that inserts the users signature into all fields field."
                          defaultMessage="Fill in all"
                        />
                      </Box>
                    </Button>
                  </Cluster.Item>
                )}

                <Cluster.Item>
                  <Button
                    variant="primary"
                    data-testid="insert-btn"
                    onClick={() => insertSignatureCallback()}
                    disabled={isDisabled}
                  >
                    <FormattedMessage
                      id="9bff1672b61adc544d8534e321758d32dac6ef7e9b0c5cc49e318ba7c7f661e9"
                      description="Text for a button that inserts the users signature into a field."
                      defaultMessage="Insert"
                    />
                  </Button>
                </Cluster.Item>
              </Cluster>
            ) : (
              <Cluster gap="Micro Small">
                <Cluster.Item>
                  <Button variant="transparent" onClick={handleClear}>
                    <FormattedMessage
                      id="60d6c024509f84a472535423afc78c49a54d00aaaff59de56825752417ab8e35"
                      description="Text for a button that clears insrted signature"
                      defaultMessage="Clear"
                    />
                  </Button>
                </Cluster.Item>

                <Cluster.Item>
                  <Button variant="opacity" onClick={() => setIsEdit(true)}>
                    <FormattedMessage
                      id="201be23645297baa2bdce7a18b4f5d21e7abe2ce928a22e5b8c260349fa3b684"
                      description="Text for a button for editing signature on signature field."
                      defaultMessage="Edit"
                    />
                  </Button>
                </Cluster.Item>
              </Cluster>
            )}
          </Box>
        </Box>
      </>
    );
  },
);

export default React.forwardRef(SignatureStandaloneFieldWrapper);
